import { keymap } from "prosemirror-keymap"
import { undo, redo, history } from "prosemirror-history"
import {
    toggleMark,
    wrapIn,
    chainCommands,
    exitCode,
    setBlockType,
    joinUp,
    joinDown,
    lift,
    selectParentNode,
    baseKeymap
} from "prosemirror-commands"
import { NodeSelection, Plugin } from "prosemirror-state"
import { dropCursor } from "prosemirror-dropcursor"
import { gapCursor } from "prosemirror-gapcursor"
import { icons, wrapItem, blockTypeItem, MenuItem, Dropdown, menuBar } from "prosemirror-menu"
import { wrapInList, splitListItem, liftListItem, sinkListItem } from "prosemirror-schema-list"
import {
    undoInputRule,
    smartQuotes,
    ellipsis,
    emDash,
    inputRules,
    wrappingInputRule,
    textblockTypeInputRule
} from "prosemirror-inputrules"

const prefix = "ProseMirror-prompt"
function openPrompt(options) {
    let wrapper = document.body.appendChild(document.createElement("div"))
    wrapper.className = prefix
    let mouseOutside = e => {
        if (!wrapper.contains(e.target)) close()
    }
    setTimeout(() => window.addEventListener("mousedown", mouseOutside), 50)
    let close = () => {
        window.removeEventListener("mousedown", mouseOutside)
        if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper)
    }
    let domFields = []
    for (let name in options.fields) domFields.push(options.fields[name].render())
    let submitButton = document.createElement("button")
    submitButton.type = "submit"
    submitButton.className = prefix + "-submit"
    submitButton.textContent = "OK"
    let cancelButton = document.createElement("button")
    cancelButton.type = "button"
    cancelButton.className = prefix + "-cancel"
    cancelButton.textContent = "Cancel"
    cancelButton.addEventListener("click", close)
    let form = wrapper.appendChild(document.createElement("form"))
    if (options.title) form.appendChild(document.createElement("h5")).textContent = options.title
    domFields.forEach(field => {
        form.appendChild(document.createElement("div")).appendChild(field)
    })
    let buttons = form.appendChild(document.createElement("div"))
    buttons.className = prefix + "-buttons"
    buttons.appendChild(submitButton)
    buttons.appendChild(document.createTextNode(" "))
    buttons.appendChild(cancelButton)
    let box = wrapper.getBoundingClientRect()
    wrapper.style.top = (window.innerHeight - box.height) / 2 + "px"
    wrapper.style.left = (window.innerWidth - box.width) / 2 + "px"
    let submit = () => {
        let params = getValues(options.fields, domFields)
        if (params) {
            close()
            options.callback(params)
        }
    }
    form.addEventListener("submit", e => {
        e.preventDefault()
        submit()
    })
    form.addEventListener("keydown", e => {
        if (e.keyCode == 27) {
            e.preventDefault()
            close()
        } else if (e.keyCode == 13 && !(e.ctrlKey || e.metaKey || e.shiftKey)) {
            e.preventDefault()
            submit()
        } else if (e.keyCode == 9) {
            window.setTimeout(() => {
                if (!wrapper.contains(document.activeElement)) close()
            }, 500)
        }
    })
    let input = form.elements[0]
    if (input) input.focus()
}
function getValues(fields, domFields) {
    let result = Object.create(null),
        i = 0
    for (let name in fields) {
        let field = fields[name],
            dom = domFields[i++]
        let value = field.read(dom),
            bad = field.validate(value)
        if (bad) {
            reportInvalid(dom, bad)
            return null
        }
        result[name] = field.clean(value)
    }
    return result
}
function reportInvalid(dom, message) {
    // FIXME this is awful and needs a lot more work
    let parent = dom.parentNode
    let msg = parent.appendChild(document.createElement("div"))
    msg.style.left = dom.offsetLeft + dom.offsetWidth + 2 + "px"
    msg.style.top = dom.offsetTop - 5 + "px"
    msg.className = "ProseMirror-invalid"
    msg.textContent = message
    setTimeout(() => parent.removeChild(msg), 1500)
}
/**
 The type of field that `openPrompt` expects to be passed to it.
 */
class Field {
    /**
     Create a field with the given options. Options support by all
     field types are:
     */
    constructor(
        /**
         @internal
         */
        options
    ) {
        this.options = options
    }
    /**
     Read the field's value from its DOM node.
     */
    read(dom) {
        return dom.value
    }
    /**
     A field-type-specific validation function.
     */
    validateType() {
        return null
    }
    /**
     @internal
     */
    validate(value) {
        if (!value && this.options.required) return "Required field"
        return this.validateType(value) || (this.options.validate ? this.options.validate(value) : null)
    }
    clean(value) {
        return this.options.clean ? this.options.clean(value) : value
    }
}
/**
 A field class for single-line text fields.
 */
class TextField extends Field {
    render() {
        let input = document.createElement("input")
        input.type = "text"
        input.placeholder = this.options.label
        input.value = this.options.value || ""
        input.autocomplete = "off"
        return input
    }
}

// Helpers to create specific types of items
function canInsert(state, nodeType) {
    let $from = state.selection.$from
    for (let d = $from.depth; d >= 0; d--) {
        let index = $from.index(d)
        if ($from.node(d).canReplaceWith(index, index, nodeType)) return true
    }
    return false
}
function insertImageItem(nodeType) {
    return new MenuItem({
        title: "Insert image",
        label: "Image",
        enable(state) {
            return canInsert(state, nodeType)
        },
        run(state, _, view) {
            let { from, to } = state.selection,
                attrs = null
            if (state.selection instanceof NodeSelection && state.selection.node.type == nodeType)
                attrs = state.selection.node.attrs
            openPrompt({
                title: "Insert image",
                fields: {
                    src: new TextField({ label: "Location", required: true, value: attrs && attrs.src }),
                    title: new TextField({ label: "Title", value: attrs && attrs.title }),
                    alt: new TextField({
                        label: "Description",
                        value: attrs ? attrs.alt : state.doc.textBetween(from, to, " ")
                    })
                },
                callback(attrs) {
                    view.dispatch(view.state.tr.replaceSelectionWith(nodeType.createAndFill(attrs)))
                    view.focus()
                }
            })
        }
    })
}
function cmdItem(cmd, options) {
    let passedOptions = {
        label: options.title,
        run: cmd
    }
    for (let prop in options) passedOptions[prop] = options[prop]
    if (!options.enable && !options.select) passedOptions[options.enable ? "enable" : "select"] = state => cmd(state)
    return new MenuItem(passedOptions)
}
function markActive(state, type) {
    let { from, $from, to, empty } = state.selection
    if (empty) return !!type.isInSet(state.storedMarks || $from.marks())
    else return state.doc.rangeHasMark(from, to, type)
}
function markItem(markType, options) {
    let passedOptions = {
        active(state) {
            return markActive(state, markType)
        }
    }
    for (let prop in options) passedOptions[prop] = options[prop]
    return cmdItem(toggleMark(markType), passedOptions)
}
function linkItem(markType) {
    return new MenuItem({
        title: "Add or remove link",
        icon: {
            path: "M17.6572 14.8282L16.2429 13.414L17.6572 11.9998C19.2193 10.4377 19.2193 7.90506 17.6572 6.34296C16.0951 4.78086 13.5624 4.78086 12.0003 6.34296L10.5861 7.75717L9.17188 6.34296L10.5861 4.92875C12.9292 2.5856 16.7282 2.5856 19.0714 4.92875C21.4145 7.27189 21.4145 11.0709 19.0714 13.414L17.6572 14.8282ZM14.8287 17.6567L13.4145 19.0709C11.0714 21.414 7.27238 21.414 4.92923 19.0709C2.58609 16.7277 2.58609 12.9287 4.92923 10.5856L6.34345 9.17139L7.75766 10.5856L6.34345 11.9998C4.78135 13.5619 4.78135 16.0946 6.34345 17.6567C7.90555 19.2188 10.4382 19.2188 12.0003 17.6567L13.4145 16.2425L14.8287 17.6567ZM14.8287 7.75717L16.2429 9.17139L9.17188 16.2425L7.75766 14.8282L14.8287 7.75717Z"
        },
        active(state) {
            return markActive(state, markType)
        },
        enable(state) {
            return !state.selection.empty
        },
        run(state, dispatch, view) {
            if (markActive(state, markType)) {
                toggleMark(markType)(state, dispatch)
                return true
            }
            openPrompt({
                title: "Create a link",
                fields: {
                    href: new TextField({
                        label: "Link target",
                        required: true
                    }),
                    title: new TextField({ label: "Title" })
                },
                callback(attrs) {
                    toggleMark(markType, attrs)(view.state, view.dispatch)
                    view.focus()
                }
            })
        }
    })
}
function wrapListItem(nodeType, options) {
    return cmdItem(wrapInList(nodeType, options.attrs), options)
}
/**
 Given a schema, look for default mark and node types in it and
 return an object with relevant menu items relating to those marks.
 */
function buildMenuItems(schema) {
    let r = {}
    let mark
    if ((mark = schema.marks.strong))
        r.toggleStrong = markItem(mark, {
            title: "Toggle strong style",
            icon: {
                path: `M17 9.5C17 9.04037 16.9095 8.58525 16.7336 8.16061C16.5577 7.73597 16.2999 7.35013 15.9749 7.02513C15.6499 6.70012 15.264 6.44231 14.8394 6.26642C14.4148 6.09053 13.9596 6 13.5 6H9C8.73478 6 8.48043 6.10536 8.29289 6.29289C8.10536 6.48043 8 6.73478 8 7V17C8 17.2652 8.10536 17.5196 8.29289 17.7071C8.48043 17.8946 8.73478 18 9 18H13.5C14.1967 18.003 14.8783 17.7975 15.4572 17.4098C16.036 17.0221 16.4856 16.4701 16.7481 15.8247C17.0105 15.1794 17.0738 14.4703 16.9298 13.7886C16.7858 13.107 16.4411 12.4841 15.94 12C16.2743 11.6749 16.5403 11.2863 16.7223 10.857C16.9044 10.4277 16.9988 9.96631 17 9.5ZM13.5 16H10V13H13.5C13.8978 13 14.2794 13.158 14.5607 13.4393C14.842 13.7206 15 14.1022 15 14.5C15 14.8978 14.842 15.2794 14.5607 15.5607C14.2794 15.842 13.8978 16 13.5 16ZM13.5 11H10V8H13.5C13.8978 8 14.2794 8.15804 14.5607 8.43934C14.842 8.72064 15 9.10218 15 9.5C15 9.89782 14.842 10.2794 14.5607 10.5607C14.2794 10.842 13.8978 11 13.5 11Z`
            }
        })
    if ((mark = schema.marks.em))
        r.toggleEm = markItem(mark, {
            title: "Toggle emphasis",
            icon: {
                path: "M17 6H11C10.7348 6 10.4804 6.10536 10.2929 6.29289C10.1054 6.48043 10 6.73478 10 7C10 7.26522 10.1054 7.51957 10.2929 7.70711C10.4804 7.89464 10.7348 8 11 8H12.52L9.32 16H7C6.73478 16 6.48043 16.1054 6.29289 16.2929C6.10536 16.4804 6 16.7348 6 17C6 17.2652 6.10536 17.5196 6.29289 17.7071C6.48043 17.8946 6.73478 18 7 18H13C13.2652 18 13.5196 17.8946 13.7071 17.7071C13.8946 17.5196 14 17.2652 14 17C14 16.7348 13.8946 16.4804 13.7071 16.2929C13.5196 16.1054 13.2652 16 13 16H11.48L14.68 8H17C17.2652 8 17.5196 7.89464 17.7071 7.70711C17.8946 7.51957 18 7.26522 18 7C18 6.73478 17.8946 6.48043 17.7071 6.29289C17.5196 6.10536 17.2652 6 17 6Z"
            }
        })

    if ((mark = schema.marks.strike))
        r.toggleStrike = markItem(mark, {
            title: "Toggle strike style",
            icon: {
                path: `M9.14167 8.79167C9.14167 8.311 9.396 7.81983 9.92917 7.42083C10.4635 7.0195 11.2382 6.75 12.1167 6.75C13.4187 6.75 14.492 7.36017 14.8747 7.9715C14.9977 8.16829 15.1938 8.30816 15.4199 8.36034C15.646 8.41253 15.8836 8.37274 16.0804 8.24975C16.2772 8.12676 16.4171 7.93062 16.4693 7.7045C16.5214 7.47838 16.4817 7.24079 16.3587 7.044C15.5735 5.78983 13.848 5 12.1167 5C10.8952 5 9.74367 5.37217 8.87917 6.02083C8.01233 6.67067 7.39167 7.639 7.39167 8.79167C7.39167 9.41233 7.5725 9.984 7.88517 10.4833H10.4717C9.56867 10.0587 9.14167 9.37383 9.14167 8.79167ZM5 12.8167C5 12.5846 5.09219 12.362 5.25628 12.1979C5.42038 12.0339 5.64294 11.9417 5.875 11.9417H18.125C18.3571 11.9417 18.5796 12.0339 18.7437 12.1979C18.9078 12.362 19 12.5846 19 12.8167C19 13.0487 18.9078 13.2713 18.7437 13.4354C18.5796 13.5995 18.3571 13.6917 18.125 13.6917H16.2175C16.466 14.1478 16.6083 14.6577 16.6083 15.2083C16.6083 16.361 15.9877 17.3282 15.1208 17.9792C14.2552 18.6278 13.1048 19 11.8833 19C10.1462 19 8.3985 18.2032 7.618 16.7997C7.55893 16.6991 7.52057 16.5876 7.50519 16.472C7.48981 16.3563 7.49772 16.2388 7.52845 16.1262C7.55917 16.0137 7.6121 15.9084 7.68411 15.8166C7.75612 15.7248 7.84577 15.6483 7.94777 15.5917C8.04977 15.535 8.16207 15.4994 8.27806 15.4868C8.39405 15.4742 8.51139 15.4849 8.62318 15.5183C8.73496 15.5517 8.83894 15.6072 8.92898 15.6814C9.01902 15.7555 9.09332 15.847 9.1475 15.9503C9.53483 16.6457 10.5872 17.25 11.8833 17.25C12.7618 17.25 13.5353 16.9805 14.0708 16.5792C14.604 16.179 14.8583 15.689 14.8583 15.2083C14.8583 14.7008 14.5328 14.1152 13.8515 13.6917H5.875C5.76009 13.6917 5.64631 13.669 5.54015 13.6251C5.43399 13.5811 5.33753 13.5166 5.25628 13.4354C5.17503 13.3541 5.11058 13.2577 5.06661 13.1515C5.02263 13.0454 5 12.9316 5 12.8167Z`
            }
        })

    if ((mark = schema.marks.underline))
        r.toggleUnderline = markItem(mark, {
            title: "Toggle underline style",
            icon: {
                path: `M12 15.5C13.3261 15.5 14.5979 14.9732 15.5355 14.0355C16.4732 13.0979 17 11.8261 17 10.5V5.5C17 5.23478 16.8946 4.98043 16.7071 4.79289C16.5196 4.60536 16.2652 4.5 16 4.5C15.7348 4.5 15.4804 4.60536 15.2929 4.79289C15.1054 4.98043 15 5.23478 15 5.5V10.5C15 11.2956 14.6839 12.0587 14.1213 12.6213C13.5587 13.1839 12.7956 13.5 12 13.5C11.2044 13.5 10.4413 13.1839 9.87868 12.6213C9.31607 12.0587 9 11.2956 9 10.5V5.5C9 5.23478 8.89464 4.98043 8.70711 4.79289C8.51957 4.60536 8.26522 4.5 8 4.5C7.73478 4.5 7.48043 4.60536 7.29289 4.79289C7.10536 4.98043 7 5.23478 7 5.5V10.5C7 11.8261 7.52678 13.0979 8.46447 14.0355C9.40215 14.9732 10.6739 15.5 12 15.5ZM17 17.5H7C6.73478 17.5 6.48043 17.6054 6.29289 17.7929C6.10536 17.9804 6 18.2348 6 18.5C6 18.7652 6.10536 19.0196 6.29289 19.2071C6.48043 19.3946 6.73478 19.5 7 19.5H17C17.2652 19.5 17.5196 19.3946 17.7071 19.2071C17.8946 19.0196 18 18.7652 18 18.5C18 18.2348 17.8946 17.9804 17.7071 17.7929C17.5196 17.6054 17.2652 17.5 17 17.5Z`
            }
        })

    if ((mark = schema.marks.link)) r.toggleLink = linkItem(mark)
    let node
    if ((node = schema.nodes.image)) r.insertImage = insertImageItem(node)
    if ((node = schema.nodes.bullet_list))
        r.wrapBulletList = wrapListItem(node, {
            title: "Wrap in bullet list",
            icon: icons.bulletList
        })
    if ((node = schema.nodes.ordered_list))
        r.wrapOrderedList = wrapListItem(node, {
            title: "Wrap in ordered list",
            icon: icons.orderedList
        })
    if ((node = schema.nodes.blockquote))
        r.wrapBlockQuote = wrapItem(node, {
            title: "Wrap in block quote",
            icon: {
                path: "M4.58341 17.3211C3.55316 16.2275 3 15 3 13.0104C3 9.51092 5.45651 6.37372 9.03059 4.82324L9.92328 6.20085C6.58804 8.00545 5.93618 10.3461 5.67564 11.8221C6.21263 11.5444 6.91558 11.4467 7.60471 11.5106C9.40908 11.6778 10.8312 13.1591 10.8312 15C10.8312 16.933 9.26416 18.5 7.33116 18.5C6.2581 18.5 5.23196 18.0096 4.58341 17.3211ZM14.5834 17.3211C13.5532 16.2275 13 15 13 13.0104C13 9.51092 15.4565 6.37372 19.0306 4.82324L19.9233 6.20085C16.588 8.00545 15.9362 10.3461 15.6756 11.8221C16.2126 11.5444 16.9156 11.4467 17.6047 11.5106C19.4091 11.6778 20.8312 13.1591 20.8312 15C20.8312 16.933 19.2642 18.5 17.3312 18.5C16.2581 18.5 15.232 18.0096 14.5834 17.3211Z"
            }
        })
    if ((node = schema.nodes.paragraph))
        r.makeParagraph = blockTypeItem(node, {
            title: "Change to paragraph",
            label: "Paragraph"
        })
    if ((node = schema.nodes.code_block))
        r.makeCodeBlock = blockTypeItem(node, {
            title: "Change to code block",
            label: "Code"
        })

    if ((mark = schema.marks.code))
        r.toggleCode = markItem(mark, {
            title: "Toggle code font",
            icon: {
                path: "M24 12L18.3431 17.6568L16.9289 16.2426L21.1716 12L16.9289 7.75735L18.3431 6.34314L24 12ZM2.82843 12L7.07107 16.2426L5.65685 17.6568L0 12L5.65685 6.34314L7.07107 7.75735L2.82843 12Z"
            }
        })

    if ((node = schema.nodes.heading))
        for (let i = 1; i <= 10; i++)
            r["makeHead" + i] = blockTypeItem(node, {
                title: "Change to heading " + i,
                label: "Level " + i,
                attrs: { level: i }
            })
    if ((node = schema.nodes.horizontal_rule)) {
        let hr = node
        r.insertHorizontalRule = new MenuItem({
            title: "Insert horizontal rule",
            label: "Horizontal rule",
            enable(state) {
                return canInsert(state, hr)
            },
            run(state, dispatch) {
                dispatch(state.tr.replaceSelectionWith(hr.create()))
            }
        })
    }
    let cut = arr => arr.filter(x => x)
    r.insertMenu = new Dropdown(cut([r.insertImage, r.insertHorizontalRule]), { label: "Insert" })
    r.typeMenu = new Dropdown(
        cut([
            r.makeParagraph,
            r.makeCodeBlock,
            ...[r.makeHead1, r.makeHead2, r.makeHead3, r.makeHead4, r.makeHead5, r.makeHead6]
            /*r.makeHead1 &&
                new DropdownSubmenu(
                    cut([r.makeHead1, r.makeHead2, r.makeHead3, r.makeHead4, r.makeHead5, r.makeHead6]),
                    { label: "Heading" }
                )*/
        ]),
        { class: "heading", title: "Heading" }
    )
    r.inlineMenu = [cut([r.toggleStrong, r.toggleEm, r.toggleUnderline, r.toggleStrike, r.toggleLink])]
    r.blockMenu = [
        cut([
            r.wrapBulletList,
            r.wrapOrderedList,

            r.wrapBlockQuote,
            r.toggleCode /*joinUpItem, liftItem, selectParentNodeItem*/
        ])
    ]
    r.fullMenu = r.inlineMenu.concat([[r.insertMenu, r.typeMenu]], /*[[undoItem, redoItem]],*/ r.blockMenu)
    return r
}

const mac = typeof navigator != "undefined" ? /Mac|iP(hone|[oa]d)/.test(navigator.platform) : false
/**
 Inspect the given schema looking for marks and nodes from the
 basic schema, and if found, add key bindings related to them.
 This will add:

 * **Mod-b** for toggling [strong](https://prosemirror.net/docs/ref/#schema-basic.StrongMark)
 * **Mod-i** for toggling [emphasis](https://prosemirror.net/docs/ref/#schema-basic.EmMark)
 * **Mod-`** for toggling [code font](https://prosemirror.net/docs/ref/#schema-basic.CodeMark)
 * **Ctrl-Shift-0** for making the current textblock a paragraph
 * **Ctrl-Shift-1** to **Ctrl-Shift-Digit6** for making the current
 textblock a heading of the corresponding level
 * **Ctrl-Shift-Backslash** to make the current textblock a code block
 * **Ctrl-Shift-8** to wrap the selection in an ordered list
 * **Ctrl-Shift-9** to wrap the selection in a bullet list
 * **Ctrl->** to wrap the selection in a block quote
 * **Enter** to split a non-empty textblock in a list item while at
 the same time splitting the list item
 * **Mod-Enter** to insert a hard break
 * **Mod-_** to insert a horizontal rule
 * **Backspace** to undo an input rule
 * **Alt-ArrowUp** to `joinUp`
 * **Alt-ArrowDown** to `joinDown`
 * **Mod-BracketLeft** to `lift`
 * **Escape** to `selectParentNode`

 You can suppress or map these bindings by passing a `mapKeys`
 argument, which maps key names (say `"Mod-B"` to either `false`, to
 remove the binding, or a new key name string.
 */
function buildKeymap(schema, mapKeys) {
    let keys = {},
        type
    function bind(key, cmd) {
        if (mapKeys) {
            let mapped = mapKeys[key]
            if (mapped === false) return
            if (mapped) key = mapped
        }
        keys[key] = cmd
    }
    bind("Mod-z", undo)
    bind("Shift-Mod-z", redo)
    bind("Backspace", undoInputRule)
    if (!mac) bind("Mod-y", redo)
    bind("Alt-ArrowUp", joinUp)
    bind("Alt-ArrowDown", joinDown)
    bind("Mod-BracketLeft", lift)
    bind("Escape", selectParentNode)
    if ((type = schema.marks.strong)) {
        bind("Mod-b", toggleMark(type))
        bind("Mod-B", toggleMark(type))
    }
    if ((type = schema.marks.em)) {
        bind("Mod-i", toggleMark(type))
        bind("Mod-I", toggleMark(type))
    }
    if ((type = schema.marks.code)) bind("Mod-`", toggleMark(type))
    if ((type = schema.nodes.bullet_list)) bind("Shift-Ctrl-8", wrapInList(type))
    if ((type = schema.nodes.ordered_list)) bind("Shift-Ctrl-9", wrapInList(type))
    if ((type = schema.nodes.blockquote)) bind("Ctrl->", wrapIn(type))
    if ((type = schema.nodes.hard_break)) {
        let br = type,
            cmd = chainCommands(exitCode, (state, dispatch) => {
                if (dispatch) dispatch(state.tr.replaceSelectionWith(br.create()).scrollIntoView())
                return true
            })
        bind("Mod-Enter", cmd)
        bind("Shift-Enter", cmd)
        if (mac) bind("Ctrl-Enter", cmd)
    }
    if ((type = schema.nodes.list_item)) {
        bind("Enter", splitListItem(type))
        bind("Mod-[", liftListItem(type))
        bind("Mod-]", sinkListItem(type))
    }
    if ((type = schema.nodes.paragraph)) bind("Shift-Ctrl-0", setBlockType(type))
    if ((type = schema.nodes.code_block)) bind("Shift-Ctrl-\\", setBlockType(type))
    if ((type = schema.nodes.heading))
        for (let i = 1; i <= 6; i++) bind("Shift-Ctrl-" + i, setBlockType(type, { level: i }))
    if ((type = schema.nodes.horizontal_rule)) {
        let hr = type
        bind("Mod-_", (state, dispatch) => {
            if (dispatch) dispatch(state.tr.replaceSelectionWith(hr.create()).scrollIntoView())
            return true
        })
    }
    return keys
}

/**
 Given a blockquote node type, returns an input rule that turns `"> "`
 at the start of a textblock into a blockquote.
 */
function blockQuoteRule(nodeType) {
    return wrappingInputRule(/^\s*>\s$/, nodeType)
}
/**
 Given a list node type, returns an input rule that turns a number
 followed by a dot at the start of a textblock into an ordered list.
 */
function orderedListRule(nodeType) {
    return wrappingInputRule(
        /^(\d+)\.\s$/,
        nodeType,
        match => ({ order: +match[1] }),
        (match, node) => node.childCount + node.attrs.order == +match[1]
    )
}
/**
 Given a list node type, returns an input rule that turns a bullet
 (dash, plush, or asterisk) at the start of a textblock into a
 bullet list.
 */
function bulletListRule(nodeType) {
    return wrappingInputRule(/^\s*([-+*])\s$/, nodeType)
}
/**
 Given a code block node type, returns an input rule that turns a
 textblock starting with three backticks into a code block.
 */
function codeBlockRule(nodeType) {
    return textblockTypeInputRule(/^```$/, nodeType)
}
/**
 Given a node type and a maximum level, creates an input rule that
 turns up to that number of `#` characters followed by a space at
 the start of a textblock into a heading whose level corresponds to
 the number of `#` signs.
 */
function headingRule(nodeType, maxLevel) {
    return textblockTypeInputRule(new RegExp("^(#{1," + maxLevel + "})\\s$"), nodeType, match => ({
        level: match[1].length
    }))
}
/**
 A set of input rules for creating the basic block quotes, lists,
 code blocks, and heading.
 */
function buildInputRules(schema) {
    let rules = smartQuotes.concat(ellipsis, emDash),
        type
    if ((type = schema.nodes.blockquote)) rules.push(blockQuoteRule(type))
    if ((type = schema.nodes.ordered_list)) rules.push(orderedListRule(type))
    if ((type = schema.nodes.bullet_list)) rules.push(bulletListRule(type))
    if ((type = schema.nodes.code_block)) rules.push(codeBlockRule(type))
    if ((type = schema.nodes.heading)) rules.push(headingRule(type, 6))
    return inputRules({ rules })
}

/**
 Create an array of plugins pre-configured for the given schema.
 The resulting array will include the following plugins:

 * Input rules for smart quotes and creating the block types in the
 schema using markdown conventions (say `"> "` to create a
 blockquote)

 * A keymap that defines keys to create and manipulate the nodes in the
 schema

 * A keymap binding the default keys provided by the
 prosemirror-commands module

 * The undo history plugin

 * The drop cursor plugin

 * The gap cursor plugin

 * A custom plugin that adds a `menuContent` prop for the
 prosemirror-menu wrapper, and a CSS class that enables the
 additional styling defined in `style/style.css` in this package

 Probably only useful for quickly setting up a passable
 editor—you'll need more control over your settings in most
 real-world situations.
 */
function exampleSetup(options) {
    let plugins = [
        buildInputRules(options.schema),
        keymap(buildKeymap(options.schema, options.mapKeys)),
        keymap(baseKeymap),
        dropCursor(),
        gapCursor()
    ]
    if (options.menuBar !== false)
        plugins.push(
            menuBar({
                floating: options.floatingMenu !== false,
                content: options.menuContent || buildMenuItems(options.schema).fullMenu
            })
        )
    if (options.history !== false) plugins.push(history())
    return plugins.concat(
        new Plugin({
            props: {
                attributes: { class: "ProseMirror-example-setup-style" }
            }
        })
    )
}

export { buildInputRules, buildKeymap, buildMenuItems, exampleSetup }
